import { db, setMeta, getMeta, STORES, type LocalStop, type OutboxEntry } from './localdb.js';
import { drain, notify, type SendOutcome } from './outbox.js';
import { attachmentIdFor, releaseConfirmed, uploadPending } from './media.js';
import { apiFetch, tenantSlug } from './api.js';
import { startReporting } from './geo.js';

/**
 * Synchronisation.
 *
 * Three directions, deliberately separate and deliberately in this order:
 *
 *   **Push** drains the outbox. Unsent work leaving the phone matters more than the phone
 *   having fresher data, because the connection is about to die again.
 *
 *   **Upload** pushes the bytes — captures, in resumable chunks. After the queue rather than
 *   inside it: a 400 KB photograph must not hold up the sentence that says a stop is done.
 *
 *   **Pull** refills the working set. Seven days back, fourteen ahead, this technician's
 *   stops, and everything those stops imply — the dwellings, their equipment, their open
 *   hazards, and the whole of the business's vocabulary. One composite response, one
 *   transaction on the phone.
 *
 * A failed pull is not a failed sync. The push may well have succeeded, and the local data
 * simply stays as fresh as it was.
 */

export type SyncResult = {
  pushed: number;
  pushFailed: number;
  pushRejected: number;
  uploaded: number;
  pulled: { stops: number; places: number; customers: number } | null;
  at: string;
  error: string | null;
};

type WorkingSet = {
  syncedAt: string;
  window: { from: string; to: string };
  userId: string;
  tenantId: string;
  capabilities: string[];
  settings: Record<string, unknown>;
  stops: LocalStop[];
  places: Array<Record<string, unknown>>;
  buildings: Array<Record<string, unknown>>;
  customers: Array<Record<string, unknown>>;
  invoices: Array<Record<string, unknown>>;
  messages: Array<Record<string, unknown>>;
  timeEntries: Array<Record<string, unknown>>;
  crew: Array<Record<string, unknown>>;
  notices: Array<Record<string, unknown>>;
  reference: Record<string, unknown>;
};

/**
 * Send one queued entry, and classify the failure.
 *
 * The classification is the whole contract with the outbox: `permanent` means the request
 * itself is the problem and retrying will fail identically, so it goes to the *needs
 * attention* list instead of round and round.
 */
async function sendEntry(entry: OutboxEntry): Promise<SendOutcome> {
  let response: Response;
  try {
    response = await apiFetch(entry.path, {
      method: entry.method,
      headers: {
        'content-type': 'application/json',
        // The server stores this key and replays the original response, so a retry after an
        // ambiguous timeout cannot double-post.
        'idempotency-key': entry.idempotencyKey,
      },
      body: JSON.stringify(entry.body),
    });
  } catch {
    // Network failure: worth retrying, and says nothing about the request's validity.
    return { ok: false, permanent: false, reason: 'network' };
  }

  if (response.ok) return { ok: true };

  const body = (await response.json().catch(() => null)) as { error?: string; message?: string } | null;

  /*
   * 409 on a transition is a special case worth naming: the stop is not in the state this
   * entry assumed, usually because the office moved it while the phone was underground.
   * Permanent — replaying will not change the server's mind — and the *needs attention* list
   * is exactly where a technician should see it, with the reason in French.
   */
  const permanent =
    response.status >= 400 && response.status < 500 && response.status !== 401 && response.status !== 429;

  return {
    ok: false,
    permanent,
    reason: body?.message ?? body?.error ?? `HTTP ${response.status}`,
  };
}

export async function sync(): Promise<SyncResult> {
  const at = new Date().toISOString();
  const empty: SyncResult = { pushed: 0, pushFailed: 0, pushRejected: 0, uploaded: 0, pulled: null, at, error: null };

  if (!navigator.onLine) return { ...empty, error: 'offline' };
  if (!(await tenantSlug())) return { ...empty, error: 'no_tenant' };

  // 1. Unsent work leaves first.
  const push = await drain(sendEntry, attachmentIdFor);

  // 2. Then the bytes. Anything the queue was holding for a capture will go on the next pass,
  //    which the caller triggers — a photograph that finishes at the end of this sync should
  //    not wait five minutes for the reading that refers to it.
  const media = await uploadPending();
  const second = media.uploaded > 0 ? await drain(sendEntry, attachmentIdFor) : { sent: 0, failed: 0, rejected: 0 };

  let pulled: SyncResult['pulled'] = null;
  let error: string | null = null;

  try {
    const response = await apiFetch('/field/working-set');
    if (response.status === 401) throw new Error('unauthenticated');
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const set = (await response.json()) as WorkingSet;
    pulled = await replaceWorkingSet(set);

    await setMeta('lastSyncedAt', at);
    await setMeta('workingSetFrom', set.window.from);
    await setMeta('workingSetTo', set.window.to);
    await setMeta('userId', set.userId);
    await setMeta('tenantId', set.tenantId);
    await setMeta('capabilities', JSON.stringify(set.capabilities));

    // Position follows the business's switch, checked on every sync rather than at start-up:
    // a business that turns it off should not have to wait for the vans to be restarted.
    void startReporting(Boolean(set.settings?.positionEnabled));
  } catch (e) {
    error = e instanceof Error ? e.message : 'pull_failed';
  }

  notify();
  return {
    pushed: push.sent + second.sent,
    pushFailed: push.failed + second.failed,
    pushRejected: push.rejected + second.rejected,
    uploaded: media.uploaded,
    pulled,
    at,
    error,
  };
}

/**
 * Replace the working set with what the server sent.
 *
 * Wholesale replacement rather than merging, because the server's view is authoritative for
 * everything except the outbox and the media — which live in their own stores and are
 * untouched here. That is what makes the sync idempotent and impossible to leave half-applied:
 * one transaction, all of it or none.
 *
 * Two things survive the replacement, and they are the two the server does not know about:
 * a **pending mark** on any row the outbox still holds work for, and any **local-only** row —
 * a reading taken in a crawlspace two minutes ago is not in the server's answer, and dropping
 * it would look exactly like losing it.
 */
async function replaceWorkingSet(set: WorkingSet): Promise<{ stops: number; places: number; customers: number }> {
  const database = await db();

  const outstanding = await database.getAll('outbox');
  const pendingTargets = new Set(outstanding.map((e) => e.targetId).filter((id): id is string => Boolean(id)));

  // Captures still on this phone, so the stop's gallery shows them beside the ones that have
  // landed rather than appearing to have lost them.
  const localMedia = await database.getAll('media');
  const localByStop = new Map<string, typeof localMedia>();
  for (const media of localMedia) {
    if (media.attachmentId) continue;
    const list = localByStop.get(media.appointmentId) ?? [];
    list.push(media);
    localByStop.set(media.appointmentId, list);
  }

  const tx = database.transaction(
    ['stops', 'places', 'buildings', 'customers', 'invoices', 'messages', 'timeEntries', 'reference'],
    'readwrite',
  );

  const stops = tx.objectStore('stops');
  const previous = new Map((await stops.getAll()).map((stop) => [stop.id, stop]));
  await stops.clear();
  for (const stop of set.stops) {
    const local = localByStop.get(stop.id) ?? [];
    const before = previous.get(stop.id);
    // Local readings the server has not acknowledged yet, kept beside its own.
    const unsentReadings = (before?.readings ?? []).filter((reading) => reading.pending);

    await stops.put({
      ...stop,
      pending: pendingTargets.has(stop.id) || local.length > 0 || unsentReadings.length > 0,
      captures: [
        ...stop.captures,
        ...local.map((media) => ({
          id: media.id,
          kind: media.kind,
          caption: media.caption,
          contentType: media.contentType,
          capturedAt: media.capturedAt,
          transcript: null,
          takenBy: null,
          pending: true,
        })),
      ],
      readings: [...stop.readings, ...unsentReadings],
    });
  }

  const places = tx.objectStore('places');
  await places.clear();
  for (const place of set.places) await places.put(place as never);

  const buildings = tx.objectStore('buildings');
  await buildings.clear();
  for (const building of set.buildings) await buildings.put(building as never);

  const customers = tx.objectStore('customers');
  await customers.clear();
  for (const customer of set.customers) await customers.put(customer as never);

  const invoices = tx.objectStore('invoices');
  await invoices.clear();
  for (const invoice of set.invoices) await invoices.put(invoice as never);

  // Messages composed offline are in the outbox, not here, but the optimistic copies we
  // wrote locally must survive — they are what the technician can see in the thread.
  const messages = tx.objectStore('messages');
  const localMessages = (await messages.getAll()).filter((m) => m.pending);
  await messages.clear();
  for (const message of set.messages) await messages.put(message as never);
  for (const message of localMessages) await messages.put(message);

  const timeEntries = tx.objectStore('timeEntries');
  const localTime = (await timeEntries.getAll()).filter((t) => t.pending);
  await timeEntries.clear();
  for (const entry of set.timeEntries) await timeEntries.put(entry as never);
  for (const entry of localTime) await timeEntries.put(entry);

  const reference = tx.objectStore('reference');
  for (const [key, value] of Object.entries(set.reference)) {
    await reference.put({ key: key as never, value });
  }
  await reference.put({ key: 'crew', value: set.crew });
  await reference.put({ key: 'settings', value: set.settings });

  /*
   * The server's view of what this person has agreed to wins — except where the outbox is
   * still carrying an acknowledgement it has not seen yet. Dropping that would put a blocking
   * notice back in front of somebody who accepted it in a crawlspace twenty minutes ago.
   */
  const pendingAcks = new Set(
    outstanding
      .filter((entry) => entry.entity === 'notice')
      .map((entry) => (entry.body as { versionId?: string } | null)?.versionId)
      .filter((id): id is string => Boolean(id)),
  );
  await reference.put({
    key: 'notices',
    value: (set.notices ?? []).map((notice) => {
      const row = notice as Record<string, unknown>;
      return pendingAcks.has(String(row.versionId)) && !row.acknowledgedAt
        ? { ...row, acknowledgedAt: new Date().toISOString() }
        : row;
    }),
  });

  await tx.done;

  /*
   * Let go of the captures the office demonstrably holds.
   *
   * Only now, and only because they came back in the working set. The upload reporting success
   * is not the same thing: that round trip may have been the last one before the tunnel, and
   * the phone gives up the only copy of a photograph exactly once.
   */
  const confirmed = new Set(set.stops.flatMap((stop) => stop.captures.map((capture) => capture.id)));
  await releaseConfirmed(confirmed);

  return { stops: set.stops.length, places: set.places.length, customers: set.customers.length };
}

/**
 * When to sync.
 *
 * Background Sync handles the reconnect case on Android, which is where the technicians are.
 * The other triggers cover everywhere it is unavailable or unreliable — notably iOS — so the
 * queue always drains, just less promptly.
 *
 * The five-minute heartbeat is the backstop and nothing else: it is far too slow for a stop
 * added at 10:02 for 10:30, which is what the WebSocket in `realtime.ts` is for.
 */
export function startAutoSync(onResult: (result: SyncResult) => void): () => void {
  let timer: number | undefined;

  const run = () => {
    void sync().then(onResult);
  };

  const onOnline = () => run();
  const onVisible = () => {
    if (document.visibilityState === 'visible') run();
  };

  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisible);
  timer = window.setInterval(run, 5 * 60_000);
  run();

  return () => {
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisible);
    if (timer) window.clearInterval(timer);
  };
}

export async function lastSyncedAt(): Promise<Date | null> {
  const value = await getMeta('lastSyncedAt');
  return value ? new Date(value) : null;
}

/**
 * Wipe the phone, whatever it is holding.
 *
 * Called when the server says this device is revoked. `clearLocalData()`'s refusal to wipe
 * while the outbox holds work — right in every other case — is overridden here: a lost
 * phone's unsent hours are re-entered, and its customer list is not negotiable.
 */
export async function wipeForRevoke(): Promise<void> {
  const database = await db();
  const tx = database.transaction(STORES, 'readwrite');
  await Promise.all([...STORES.map((name) => tx.objectStore(name).clear()), tx.done]);
  notify();
}
